import { useState, useCallback, useEffect, useRef } from 'react'
import { useAgent } from 'agents/react'
import {
  Play,
  Pause,
  XCircle,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader,
  ChevronDown,
  ChevronRight,
  Rocket,
  Terminal,
  GitMerge,
  Shield,
  FileText,
} from 'lucide-react'
import { AGENT_NAMES, DEFAULT_INSTANCE } from '../lib/agent-api'
import { AGENT_WORKER_HOST, isDev } from '../lib/config'

// ─── Types ───────────────────────────────────────────────────────────────

interface Phase {
  name: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  result?: string
  startedAt?: string
  completedAt?: string
}

interface LongRunningTask {
  taskId: string
  prompt: string
  status: 'queued' | 'running' | 'completed' | 'failed' | 'paused'
  progress: number
  currentPhase: string
  phases: Phase[]
  startedAt: string
  estimatedEndTime?: string
  lastActivity: string
  error?: string
}

interface AgentState {
  id: string
  name: string
  status: string
  lastActivity: string
  metadata: Record<string, unknown> & {
    longRunningTask?: LongRunningTask
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────

const phaseIcons: Record<string, any> = {
  planning: FileText,
  analysis: Terminal,
  coding: Rocket,
  testing: Shield,
  review: GitMerge,
  deployment: Rocket,
}

const phaseIconDefault = Terminal

const statusBadgeColors: Record<string, string> = {
  queued: 'bg-yellow-900 text-yellow-300',
  running: 'bg-blue-900 text-blue-300 animate-pulse',
  completed: 'bg-green-900 text-green-300',
  failed: 'bg-red-900 text-red-300',
  paused: 'bg-orange-900 text-orange-300',
}

const phaseStatusColors: Record<string, string> = {
  pending: 'text-gray-500',
  running: 'text-blue-400',
  completed: 'text-green-400',
  failed: 'text-red-400',
}

const statusIcons: Record<string, any> = {
  queued: Clock,
  running: Loader,
  completed: CheckCircle,
  failed: AlertCircle,
  paused: Pause,
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

function getElapsed(from: string): string {
  const start = new Date(from).getTime()
  const now = Date.now()
  return formatDuration(now - start)
}

// ─── Progress Bar Component ──────────────────────────────────────────────

function ProgressBar({ progress, status }: { progress: number; status: string }) {
  const barColor =
    status === 'completed'
      ? 'bg-green-500'
      : status === 'failed'
        ? 'bg-red-500'
        : status === 'paused'
          ? 'bg-orange-500'
          : 'bg-orange-500'

  return (
    <div className="w-full">
      <div className="flex justify-between text-sm mb-2">
        <span className="text-gray-400">Overall Progress</span>
        <span className="font-semibold text-white">{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

// ─── Phase Accordion Item ────────────────────────────────────────────────

function PhaseItem({ phase, index }: { phase: Phase; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = phaseIcons[phase.name.toLowerCase()] ?? phaseIconDefault
  const StatusIcon = statusIcons[phase.status] ?? Clock

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 font-mono w-6">#{index + 1}</span>
          <Icon className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-medium">{phase.name}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <StatusIcon className={`w-3.5 h-3.5 ${phaseStatusColors[phase.status]}`} />
            <span className={`text-xs capitalize ${phaseStatusColors[phase.status]}`}>
              {phase.status}
            </span>
          </div>
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 bg-gray-850 border-t border-gray-700 text-sm space-y-2">
          {phase.startedAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">Started</span>
              <span className="text-gray-300">{new Date(phase.startedAt).toLocaleString()}</span>
            </div>
          )}
          {phase.completedAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">Completed</span>
              <span className="text-gray-300">{new Date(phase.completedAt).toLocaleString()}</span>
            </div>
          )}
          {phase.result && (
            <div className="mt-2">
              <span className="text-gray-500 block mb-1">Result</span>
              <p className="text-gray-300 bg-gray-800 rounded p-2 text-xs font-mono whitespace-pre-wrap">
                {phase.result}
              </p>
            </div>
          )}
          {!phase.result && phase.status === 'pending' && (
            <p className="text-gray-600 italic">Waiting to start...</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main Page Component ─────────────────────────────────────────────────

export default function LongRunningTask() {
  const [showStartForm, setShowStartForm] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [durationHours, setDurationHours] = useState(12)
  const lastTaskRef = useRef<string | null>(null)

  const agentHost = isDev ? undefined : AGENT_WORKER_HOST

  const { state, send, identified } = useAgent<AgentState>({
    agent: 'orchestrator-agent',
    name: DEFAULT_INSTANCE,
    host: agentHost,
  })

  // Extract task state from agent metadata
  const task: LongRunningTask | null =
    (state?.metadata?.longRunningTask as LongRunningTask | undefined) ?? null

  // Track if we have received a task to avoid showing stale form
  useEffect(() => {
    if (task?.taskId && task.taskId !== lastTaskRef.current) {
      lastTaskRef.current = task.taskId
      setShowStartForm(false)
    }
  }, [task?.taskId])

  // ─── Control actions via agent RPC ───────────────────────────────────

  const startTask = useCallback(() => {
    if (!prompt.trim()) return
    
    if (!identified) {
      alert('未连接到 OrchestratorAgent，请检查网络连接后重试')
      return
    }
    
    send(
      JSON.stringify({
        type: 'startLongRunningTask',
        prompt: prompt.trim(),
        sessionId: `session-${Date.now()}`,
        durationHours,
      })
    )
    setShowStartForm(false)
    setPrompt('')
  }, [identified, send, prompt, durationHours])

  const pauseTask = useCallback(() => {
    send(JSON.stringify({ type: 'pauseLongRunningTask' }))
  }, [send])

  const resumeTask = useCallback(() => {
    send(JSON.stringify({ type: 'resumeLongRunningTask' }))
  }, [send])

  const cancelTask = useCallback(() => {
    send(JSON.stringify({ type: 'cancelLongRunningTask' }))
  }, [send])

  // ─── Render ──────────────────────────────────────────────────────────

  const isActive = task && (task.status === 'running' || task.status === 'queued')
  const isPaused = task?.status === 'paused'
  const isTerminal = task?.status === 'completed' || task?.status === 'failed'

  // Show start form if no active task
  if (!task) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Long Running Task</h2>
          <button
            onClick={() => setShowStartForm(!showStartForm)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors font-medium"
          >
            <Rocket className="w-4 h-4" />
            Start New Task
          </button>
        </div>

        {showStartForm && (
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
            <h3 className="text-lg font-semibold mb-4">Configure Long Running Task</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Task Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500 h-32 resize-none"
                  placeholder="Describe the task for the OrchestratorAgent..."
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Duration (hours): <span className="text-orange-400 font-semibold">{durationHours}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={24}
                  value={durationHours}
                  onChange={(e) => setDurationHours(Number(e.target.value))}
                  className="w-full accent-orange-500"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1h</span>
                  <span>12h</span>
                  <span>24h</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={startTask}
                  disabled={!prompt.trim() || !identified}
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors font-medium"
                >
                  {!identified ? '未连接' : '启动任务'}
                </button>
                <button
                  onClick={() => setShowStartForm(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
          <Rocket className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No active long-running task</p>
          <p className="text-gray-600 text-sm mt-2">
            Click "Start New Task" to begin a continuous OrchestratorAgent session
          </p>
        </div>
      </div>
    )
  }

  const StatusIcon = statusIcons[task.status] ?? Clock
  const elapsed = getElapsed(task.startedAt)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">Long Running Task</h2>
          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize flex items-center gap-1.5 ${statusBadgeColors[task.status]}`}>
            <StatusIcon className="w-3.5 h-3.5" />
            {task.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isTerminal && (
            <>
              {isPaused ? (
                <button
                  onClick={resumeTask}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-sm font-medium"
                >
                  <Play className="w-4 h-4" />
                  Resume
                </button>
              ) : isActive ? (
                <button
                  onClick={pauseTask}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors text-sm font-medium"
                >
                  <Pause className="w-4 h-4" />
                  Pause
                </button>
              ) : null}
              <button
                onClick={cancelTask}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors text-sm font-medium"
              >
                <XCircle className="w-4 h-4" />
                Cancel
              </button>
            </>
          )}
          {isTerminal && (
            <button
              onClick={() => {
                lastTaskRef.current = null
              }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors text-sm font-medium"
            >
              <Rocket className="w-4 h-4" />
              New Task
            </button>
          )}
        </div>
      </div>

      {/* Task Metadata */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500 block text-xs uppercase tracking-wider">Task ID</span>
            <span className="text-gray-200 font-mono text-xs">{task.taskId}</span>
          </div>
          <div>
            <span className="text-gray-500 block text-xs uppercase tracking-wider">Started</span>
            <span className="text-gray-200">{new Date(task.startedAt).toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500 block text-xs uppercase tracking-wider">Elapsed</span>
            <span className="text-gray-200">{elapsed}</span>
          </div>
          <div>
            <span className="text-gray-500 block text-xs uppercase tracking-wider">Est. Completion</span>
            <span className="text-gray-200">
              {task.estimatedEndTime
                ? new Date(task.estimatedEndTime).toLocaleString()
                : '—'}
            </span>
          </div>
        </div>
        {task.prompt && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <span className="text-gray-500 block text-xs uppercase tracking-wider mb-1">Prompt</span>
            <p className="text-gray-300 text-sm">{task.prompt}</p>
          </div>
        )}
        {task.error && (
          <div className="mt-4 p-3 bg-red-900/30 border border-red-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-red-300 text-sm font-medium">Error</span>
            </div>
            <p className="text-red-300 text-sm mt-1 ml-6">{task.error}</p>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <ProgressBar progress={task.progress} status={task.status} />
        {task.currentPhase && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-gray-500 text-sm">Current Phase:</span>
            <span className="text-orange-400 font-medium">{task.currentPhase}</span>
          </div>
        )}
      </div>

      {/* Phases */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold mb-4">Phases ({task.phases.length})</h3>
        <div className="space-y-2">
          {task.phases.map((phase, idx) => (
            <PhaseItem key={`${phase.name}-${idx}`} phase={phase} index={idx} />
          ))}
          {task.phases.length === 0 && (
            <p className="text-gray-500 text-center py-6">No phases configured yet</p>
          )}
        </div>
      </div>
    </div>
  )
}
